/**
 * The techniques a planned graphic must use, and the shortlist a chooser picks them from.
 *
 * The shortlist is the whole library on purpose: narrowing it before the chooser sees it caps what
 * a video can reach.
 */
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import { HTML_DESIGN_SKILL_META, PLAIN_TECHNIQUES } from './skillMeta';
import { getHtmlDesignSkills } from './skillRegistry';

export interface RequiredTechnique {
    skill: string;
    summary: string;
    for: string;
}

export interface TechniqueAssignment {
    required: RequiredTechnique[];
    instruction: string;
}

export interface TechniqueCandidate {
    skill: string;
    summary: string;
    adventurous: boolean;
    /** Already assigned to an earlier scene in this video. Offered anyway, so a genuinely repeated
     *  kind of moment can look like the one before it. */
    usedAlready: boolean;
}

export interface ShortlistInput {
    sceneDurationMillis: number;
    /** What earlier scenes in this video were assigned. */
    alreadyAssigned?: ReadonlySet<string>;
}

// Past two the assignment reads as a list rather than a decision.
const MAX_REQUIRED = 2;
const LONG_SCENE_MILLIS = 8000;

/** Held out of the shortlist so a slot is never spent on one; appended to every assignment. */
const CRAFT_STAPLES: ReadonlySet<string> = new Set([
    SKILL_NAMES.motionPolish,
    SKILL_NAMES.sustainedSceneReveal,
]);

function craftSkill(sceneDurationMillis: number): string {
    return sceneDurationMillis >= LONG_SCENE_MILLIS
        ? SKILL_NAMES.sustainedSceneReveal
        : SKILL_NAMES.motionPolish;
}

function summarize(name: string): string {
    return HTML_DESIGN_SKILL_META[name]?.indexSummary ?? name;
}

/** Plain entrances go last, so the bias toward the interesting ones is in the order as well as
 *  in the prompt. */
export function shortlist(input: ShortlistInput): TechniqueCandidate[] {
    const assigned = input.alreadyAssigned ?? new Set<string>();
    const candidates = getHtmlDesignSkills()
        .map(({ name }) => name)
        .filter((name) => !CRAFT_STAPLES.has(name))
        .map((name) => ({
            skill: name,
            summary: summarize(name),
            adventurous: !PLAIN_TECHNIQUES.has(name),
            usedAlready: assigned.has(name),
        }));
    return [
        ...candidates.filter(({ adventurous }) => adventurous),
        ...candidates.filter(({ adventurous }) => !adventurous),
    ];
}

export function assignmentFor(
    picks: { skill: string; why: string }[],
    input: ShortlistInput,
): TechniqueAssignment {
    const required: RequiredTechnique[] = picks
        .slice(0, MAX_REQUIRED)
        .map(({ skill, why }) => ({ skill, summary: summarize(skill), for: why }));
    const craft = craftSkill(input.sceneDurationMillis);
    required.push({ skill: craft, summary: summarize(craft), for: 'craft, on every graphic' });
    return { required, instruction: instructionFor(required) };
}

/** Not a fit judgement — nothing here reads the copy. It keeps a build moving. */
export function fallbackAssignment(input: ShortlistInput): TechniqueAssignment {
    const candidates = shortlist(input);
    const [first] = candidates.filter(({ usedAlready }) => !usedAlready);
    const picks = first
        ? [
              {
                  skill: first.skill,
                  why: 'assigned without a fit judgement — make it serve this frame',
              },
          ]
        : [];
    return assignmentFor(picks, input);
}

function instructionFor(required: RequiredTechnique[]): string {
    const names = required.map(({ skill }) => `\`${skill}\``).join(', ');
    const plural = required.length > 1;
    return (
        `Load ${names} with ${TOOL_NAMES.loadSkill} and implement ${plural ? 'them' : 'it'} in ` +
        `this composition. This is an assignment, not a menu: ${
            plural ? 'these are the techniques' : 'this is the technique'
        } this graphic uses. Read the implementation and build it — an entrance you wrote yourself ` +
        `is not a substitute, and a technique loaded but not applied is the same as never loading ` +
        `it. ${TOOL_NAMES.writeHtmlAsset} looks for each one in the composition you wrote and ` +
        `fails the call when it is not there. Build the frame around ${
            plural ? 'them' : 'it'
        } — the assignment was made for the frame you described, so a frame that cannot host it is ` +
        `a frame you have designed away from the plan.`
    );
}
